import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, ButtonBase, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { type FC, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BOTTOM_NAV, FONT_SIZE, GLYPH_BUTTON, ICON_SIZE, INK, LETTER_SPACING, NEUTRAL, STATUS } from "../../../../theme/tokens";
import { formatPredictedCost } from "../../../../utils/costPrediction";
import type { OpenShiftRequest, ReplaceShiftRequest, RequestOrigins } from "../../../../utils/models";
import { RequestOriginGlyph } from "../../../RequestData/RequestOrigin/RequestOriginGlyph";
import { RequestOriginViewLevel } from "../../../RequestData/RequestOrigin/requestOriginMeta";
import MetaChip from "../../../UIToolkit/Badges/MetaChip";
import { currentRequestBonus } from "../gapsPickupBonuses";
import { GapsJobBadge } from "../sheets/GapsJobBadge";
import { GapsRequestSmallPrint } from "./GapsRequestSmallPrint";
import { useGapsRequestLabels } from "./useGapsRequestLabels";

/** The header button's padding and gap (theme spacing), shared with the glyph pinned beside it so the two cannot drift. */
const HEADER_PX = 1.5;
const HEADER_PT = 1.25;
const HEADER_GAP = 1;

type Props = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	isUrgentClaim: boolean;
	/** "in 5 hours" for a shift inside the urgent window, else `null`. */
	startsIn: string | null;
	responseCount: number;
	isRoboCallRunning: boolean;
	origins: RequestOrigins | null;
	onOpenRequest: (request: OpenShiftRequest | ReplaceShiftRequest) => void;
	testId: string;
};

/**
 * A request card's headline, and the card's tap to its posted-shift sheet: the shift's hours (and "starts in" inside
 * the urgent window), then the job, the small print, and the chips that carry meaning — a bonus, charge only, an
 * agency posting, a robo call already out.
 *
 * Named by what it shows, with what a tap does as its description: a fixed label here used to replace all of that for
 * a screen reader. The description sits BESIDE the button, not in it: inside, it was also part of the name the button
 * takes from its content, and was announced twice.
 */
export const GapsRequestCardHeader: FC<Props> = ({ request, isUrgentClaim, startsIn, responseCount, isRoboCallRunning, origins, onOpenRequest, testId }) => {
	const { t } = useTranslation();
	const { sectionsOf, timeLabelOf } = useGapsRequestLabels();
	const openHintId = useId();
	const sections = useMemo(() => sectionsOf(request), [sectionsOf, request]);
	const bonus = currentRequestBonus(request);
	// The backend sends `null` for a request no agency has, not an absent key; only a real name is a posting.
	const agencyName = "agency_source_name" in request ? request.agency_source_name : undefined;
	const hasAgency = typeof agencyName === "string" && agencyName !== "";

	return (
		<Box sx={{ position: "relative" }}>
			<ButtonBase
				data-testid={`${testId}-open`}
				onClick={() => onOpenRequest(request)}
				aria-describedby={openHintId}
				sx={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					gap: HEADER_GAP,
					px: HEADER_PX,
					pt: HEADER_PT,
					pb: 1.125,
					textAlign: "left",
					"@media (hover: hover) and (pointer: fine)": { "&:hover": { bgcolor: NEUTRAL[50] } },
				}}
			>
				<Box sx={{ flex: 1, minWidth: 0, ...(origins !== null && { paddingInlineEnd: `${GLYPH_BUTTON.SIZE_PX}px` }) }}>
					<Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1, rowGap: 0.25 }}>
						<Typography
							component="span"
							data-testid={`${testId}-hours`}
							sx={{
								fontSize: FONT_SIZE.TITLE_SM,
								fontWeight: 700,
								letterSpacing: LETTER_SPACING.HEADLINE,
								fontVariantNumeric: "tabular-nums",
								color: INK.PRIMARY,
							}}
						>
							{timeLabelOf(request)}
						</Typography>
						{startsIn !== null && (
							<Typography
								component="span"
								data-testid={`${testId}-starts-in`}
								sx={{
									fontSize: FONT_SIZE.LABEL_XS,
									fontWeight: 700,
									letterSpacing: LETTER_SPACING.LABEL,
									textTransform: "uppercase",
									color: isUrgentClaim ? STATUS.ERROR.main : STATUS.WARNING.dark,
								}}
							>
								{t("gaps.requests.startsIn", { when: startsIn })}
							</Typography>
						)}
					</Box>
					<Box data-testid={`${testId}-meta`} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 0.75, rowGap: 0.5, mt: 0.5 }}>
						{sections.map((section) => (
							<GapsJobBadge key={section.jobLabel} group={section} dense dataTestId={`${testId}-job`} />
						))}
						<GapsRequestSmallPrint request={request} responseCount={responseCount} testId={testId} />
						{bonus > 0 && (
							<MetaChip size="small" tone="success" label={t("gaps.requests.bonus", { amount: formatPredictedCost(bonus) })} dataTestId={`${testId}-bonus`} />
						)}
						{request.charge_only && <MetaChip size="small" tone="primary" label={t("gaps.requests.chargeOnly")} />}
						{hasAgency && <MetaChip size="small" tone="info" label={t("gaps.requests.atAgency", { agency: agencyName })} />}
						{isRoboCallRunning && <MetaChip size="small" tone="warning" label={t("gaps.requests.roboCallRunning")} dataTestId={`${testId}-robo`} />}
					</Box>
				</Box>
				<ChevronRightIcon aria-hidden sx={{ flex: "none", fontSize: ICON_SIZE.MENU_ROW, color: INK.MUTED }} />
			</ButtonBase>
			{/* Beside the button, not in it: a button inside a button is invalid, and the glyph opens its own card. Pinned to
			    the headline's line, just inside the chevron, where the list's glyph sits after a title. */}
			{origins !== null && (
				<Box
					sx={(theme) => ({
						position: "absolute",
						top: theme.spacing(HEADER_PT),
						insetInlineEnd: `calc(${theme.spacing(HEADER_PX + HEADER_GAP)} + ${ICON_SIZE.MENU_ROW})`,
					})}
				>
					<RequestOriginGlyph request={request} origins={origins} level={RequestOriginViewLevel.Gaps} sheetBottomOffset={BOTTOM_NAV.TOTAL_HEIGHT_CSS} />
				</Box>
			)}
			<Box component="span" id={openHintId} sx={visuallyHidden}>
				{t("gaps.requests.openRequestHint")}
			</Box>
		</Box>
	);
};
